<template>
  <div class="scanner-overlay">
    <div
      class="scanner"
      @touchstart.passive="onTouchStart"
      @touchmove.prevent="onTouchMove"
      @touchend="onTouchEnd"
    >
      <div class="video-wrapper">
        <video
          ref="video"
          autoplay
          playsinline
          muted
          class="live-video"
          :style="{ transform: `scale(${zoom.toFixed(2)})` }"
        ></video>
        <button class="close-btn" @click="handleClose">X</button>
      </div>
      <button class="capture-btn" @click="capturePhoto" :disabled="capturing">
        {{ capturing ? 'Processing...' : 'Take Photo' }}
      </button>
      <div v-if="capturing" class="processing-indicator">
        Processing image with OCR...
      </div>
      <div v-if="lastResult" class="result">
        ✅ Text Extracted: {{ lastResult }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineEmits } from 'vue'
import { extractTextFromImage } from '@/utils/ocrUtils'

const emit = defineEmits(['close','scanned'])

const video = ref(null)
const capturing = ref(false)
const lastResult = ref('')

const zoom = ref(1)
let initialZoom = 1
let pinchStart = null

let videoTrack = null
let trackCapabilities = null
let stream = null

onMounted(async () => {
  console.log('LabelScanner mounted, initializing camera...');
  await initializeCamera();
})

function onTouchStart(e) {
  if (e.touches.length === 2) {
    const [t0, t1] = e.touches
    pinchStart = Math.hypot(
      t1.clientX - t0.clientX,
      t1.clientY - t0.clientY
    )
    initialZoom = zoom.value
  }
}

function onTouchMove(e) {
  if (e.touches.length === 2 && pinchStart) {
    const [t0, t1] = e.touches
    const dist = Math.hypot(
      t1.clientX - t0.clientX,
      t1.clientY - t0.clientY
    )
    zoom.value = Math.min(Math.max(initialZoom * (dist / pinchStart), 1), 5)
    if (videoTrack && trackCapabilities.zoom) {
      const z = Math.min(
        Math.max(zoom.value, trackCapabilities.zoom.min),
        trackCapabilities.zoom.max
      )
      videoTrack.applyConstraints({ advanced: [{ zoom: z }] }).catch(() => {})
    }
    if (videoTrack && trackCapabilities.focusMode?.includes('continuous')) {
      videoTrack
        .applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
        .catch(() => {})
    }
  }
}

function onTouchEnd() {
  pinchStart = null
}

async function initializeCamera() {
  try {
    console.log('Starting label scanner camera...');
    stream = await navigator.mediaDevices.getUserMedia({
      video: { 
        facingMode: 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    })
    
    video.value.srcObject = stream
    videoTrack = stream.getVideoTracks()[0]

    if (videoTrack?.getCapabilities) {
      trackCapabilities = videoTrack.getCapabilities()
      if (trackCapabilities.zoom) {
        // Set zoom to a lower value for label scanning (more zoomed out)
        const minZoom = trackCapabilities.zoom.min
        const maxZoom = trackCapabilities.zoom.max
        const zoomOutValue = minZoom + (maxZoom - minZoom) * 0.2 // 20% from minimum (more zoomed out)
        videoTrack.applyConstraints({ advanced: [{ zoom: zoomOutValue }] }).catch(() => {})
      }
      if (trackCapabilities.focusMode?.includes('continuous')) {
        videoTrack
          .applyConstraints({ advanced: [{ focusMode: 'continuous' }] })
          .catch(() => {})
      }
    }
  } catch (error) {
    console.error('Error starting label scanner camera:', error);
    alert('Failed to start camera. Please check camera permissions.');
  }
}

async function capturePhoto() {
  if (!video.value || video.value.readyState !== 4) {
    alert('Camera not ready. Please wait a moment and try again.');
    return;
  }

  capturing.value = true;
  lastResult.value = '';

  try {
    // Create canvas to capture the photo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions to match video
    const w = video.value.videoWidth;
    const h = video.value.videoHeight;
    canvas.width = w;
    canvas.height = h;
    
    // Apply zoom if needed
    const cropW = w / zoom.value;
    const cropH = h / zoom.value;
    const offsetX = (w - cropW) / 2;
    const offsetY = (h - cropH) / 2;
    
    // Draw the video frame to canvas
    ctx.drawImage(video.value, offsetX, offsetY, cropW, cropH, 0, 0, w, h);
    
    // Convert canvas to data URL for OCR processing
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    // Process with OCR using existing utility
    const text = await extractTextFromImage(imageDataUrl);
    
    if (text && text.trim()) {
      lastResult.value = text;
      emit('scanned', text);
    } else {
      alert('No text found in the image. Please try again with a clearer image.');
    }
    
  } catch (error) {
    console.error('Error capturing photo:', error);
    alert('Error capturing photo. Please try again.');
  } finally {
    capturing.value = false;
  }
}

function stopAll() {
  if (stream) {
    console.log('Stopping label scanner camera tracks');
    stream.getTracks().forEach(t => {
      t.stop();
      console.log('Stopped track:', t.kind);
    });
    stream = null;
  }
  capturing.value = false;
}

function handleClose() {
  stopAll()
  emit('close')
}

onBeforeUnmount(stopAll)
</script>

<style scoped>
/* Full‐screen semi‐opaque overlay */
.scanner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

/* Inner scanner box */
.scanner {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #000;
  border-radius: 8px;
  padding: 1rem;
  max-width: 90vw;
  max-height: 90vh;
  box-shadow: 0 0 12px rgba(0,0,0,0.5);
}

.video-wrapper {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.live-video {
  width: 100%;
  height: auto;
  max-height: 60vh;
  object-fit: cover;
  background: #111;
  border-radius: 8px;
}

.close-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(255,255,255,0.8);
  border: none;
  font-size: 1.3rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  cursor: pointer;
}

.capture-btn {
  margin-top: 1rem;
  padding: 0.6rem 1.2rem;
  font-size: 1rem;
  border: none;
  border-radius: 4px;
  background: #0c8687;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;
}

.capture-btn:hover {
  background: #0a7273;
}

.capture-btn:disabled {
  background: #555;
  cursor: not-allowed;
}

.processing-indicator {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: white;
}

.result {
  margin-top: 0.75rem;
  font-weight: bold;
  color: #4caf50;
  max-width: 100%;
  word-wrap: break-word;
  text-align: center;
}
</style>
